import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, Container, CssBaseline, Link, Stack, ThemeProvider, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import { AppLogo } from '@/common/components'
import { isEmailFirstLoginEnabled } from '@/common/helpers/auth/authProviderDetection'
import { useToolBoxTreatments } from '@/common/hooks'
import { useAuth } from '@/common/hooks/useAuth'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'
import { getProductDetails, Paths } from '@/common/utils'

// Match the TechnicalBackground styling from TbxLoadingPage
const TechnicalBackground = styled(Box)(({ theme }) => ({
    background: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
}))

// Match the TechnicalContainer styling from TbxLoadingPage
const TechnicalContainer = styled(Container)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: '8px',
    padding: theme.spacing(4),

    '&::after': {
        content: '"DRAWING NO: TT-SIGN-001 | SCALE: 1:1 | REV: A"',
        position: 'absolute',
        bottom: -12,
        right: 24,
        fontSize: '12px',
        color: theme.palette.text.secondary,
        background: theme.palette.background.paper,
        padding: '2px 8px',
        border: `1px solid ${theme.palette.action.disabled}`,
        borderRadius: '4px',
    },
}))

// Match the TechnicalProgressContainer styling from TbxLoadingPage
const TechnicalContentContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.active}`,
    padding: theme.spacing(3),
    borderRadius: '8px',
}))

// Match the TechnicalTypography styling from TbxLoadingPage
const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

const RELOAD_SENTINEL = 'qm_signout_reloaded'

const SignOut = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { showLibellulaWhitelabelling, showStrikerWhitelabelling } = useToolBoxTreatments()
    const productDetails = getProductDetails()
    const [isReady, setIsReady] = useState(false)
    const { login, logout } = useAuth()

    // On first arrival, drive the logout flow itself: useAuth.logout() purges
    // local state and redirects via Kinde's /logout endpoint, which returns to
    // /signout for a fresh full page load. The sentinel marks that round-trip
    // as complete; on the second mount we just render the UI. Centralising the
    // logout call here (instead of in click handlers) means /signout is the
    // single page where Kinde's isAuthenticated transition happens — and since
    // /signout sits outside UserRequiredContainer's wrapping, there's no race
    // for the unauthenticated branch to silently SSO the user back (QM-3364).
    useEffect(() => {
        if (sessionStorage.getItem(RELOAD_SENTINEL) === 'true') {
            // Sentinel stays alive until the user clicks "click here" — so that
            // a stray 401 redirect back to /signout doesn't re-trigger the
            // logout cycle. The click handler removes it on sign-in intent.
            setIsReady(true)
            return
        }
        sessionStorage.setItem(RELOAD_SENTINEL, 'true')
        logout().catch((err) => {
            console.error('[SignOut] logout failed; rendering anyway:', err)
            setIsReady(true)
        })
        // logout is stable per-provider for the lifetime of AuthContext; safe
        // to omit from deps to keep this a one-shot mount effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    // Don't render until post-reload purge is confirmed
    if (!isReady) return null

    /**
     * Handle sign in click.
     * When email-first flow is enabled, redirect to /login for provider detection.
     * When disabled, force a fresh authentication prompt:
     * - `prompt: 'login'` defeats silent SSO from any persistent auth-provider
     *   cookie that survived logout (QM-3364).
     * - history.replaceState swaps the URL to '/' before login() so Kinde's
     *   URL-capture stores '/' as the post-auth return path instead of
     *   '/signout' (QM-3364).
     */
    const handleSignInClicked = () => {
        // Clear the reload sentinel so the next visit to /signout will purge again
        sessionStorage.removeItem(RELOAD_SENTINEL)

        if (isEmailFirstLoginEnabled()) {
            navigate(Paths.LOGIN_PATHNAME)
            return
        }

        window.history.replaceState({}, '', '/')
        login({ prompt: 'login' })
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TechnicalBackground>
                <TechnicalContainer maxWidth="md">
                    <Stack spacing={4}>
                        <AppLogo logoUrl={productDetails.logo} />

                        {/* Technical Content Section */}
                        <TechnicalContentContainer>
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="center"
                                mb={2}
                            >
                                <TechnicalTypography
                                    color={theme.palette.text.secondary}
                                    sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                                    variant="body2"
                                >
                                    {t('AUTHENTICATION STATUS')}
                                </TechnicalTypography>
                            </Stack>

                            {/* Sign Out Content */}
                            <Stack
                                alignItems="center"
                                mt={3}
                                spacing={3}
                            >
                                <TechnicalTypography
                                    align="center"
                                    color={theme.palette.text.primary}
                                    textTransform={'uppercase'}
                                    variant="strong1"
                                >
                                    {t('Successfully signed out')}
                                </TechnicalTypography>
                                <TechnicalTypography
                                    align="center"
                                    color={theme.palette.text.secondary}
                                    variant="body1"
                                >
                                    {t('To sign back in') + ' '}
                                    <Link
                                        color="primary"
                                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                                        onClick={handleSignInClicked}
                                    >
                                        {t('click here')}
                                    </Link>
                                </TechnicalTypography>
                            </Stack>
                        </TechnicalContentContainer>
                    </Stack>
                </TechnicalContainer>
            </TechnicalBackground>
        </ThemeProvider>
    )
}

export default SignOut
